import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/store/authStore'
import { camerasApi, pushPreferencesApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { pushMuteInputsOf } from '@/lib/pushBell'
import { myCameraFiltersOf } from '@/lib/myPush'

/**
 * #337 — the inputs of the push mutes, for `shouldRingBell` / `muteReason` (lib/pushBell.ts): the user's push
 * categories (#138), each camera's own filter (CP 159) and the cameras whose push is off (CP 164). The two cached
 * queries the WebSocketProvider has always read (same keys and options, so no extra fetch): the provider decides
 * with them what to announce, and the Dashboard's Recent events rows show with them what was silenced.
 *
 * CP 391 — and the person's own camera rows ("My phone alerts", GET /push-preferences/cameras/): `myCameraFilters`
 * is null while that list is loading (the bell then treats the preferences as not loaded), camera id -> stored
 * filters once loaded. If the list cannot be read and nothing was loaded before, it stays null, exactly like "not
 * loaded": an empty map would let the account preferences mute a camera whose own row is "All motion", so the bell
 * would stay silent for an alert the phone received. A failed refetch keeps the rows already loaded.
 *
 * `poll: false` (the Dashboard): read what the provider keeps fresh, with no poll timer of its own (every observer
 * with a refetchInterval runs its own, so a second one would fetch the camera list twice a minute) and no refetch on
 * mount. It still loads the lists if nothing has yet.
 */
export function usePushMutes({ poll = true }: { poll?: boolean } = {}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated) === true
  const readOnly = poll ? {} : { refetchInterval: false as const, refetchOnMount: false }
  const { data: prefs } = useQuery({
    queryKey: ['push-preferences'],
    queryFn: () => pushPreferencesApi.get(),
    enabled: isAuthenticated,
    staleTime: 60_000,
    ...readOnly,
  })
  // CP 159 — shares the cameras query key, so this rides an existing fetch rather than adding one
  const { data: cameras } = useQuery({
    queryKey: queryKeys.cameras.all(),
    queryFn: () => camerasApi.getAll(),
    enabled: isAuthenticated,
    ...queryOptions.cameras.all,
    ...readOnly,
  })
  // CP 391 — same staleTime as the account preferences; the camera box and the group dialog refresh it after a save
  const { data: rows } = useQuery({
    queryKey: queryKeys.pushPreferences.cameras(),
    queryFn: () => pushPreferencesApi.getCameras(),
    enabled: isAuthenticated,
    staleTime: 60_000,
    ...readOnly,
  })
  const { cameraFilters, mutedCameraIds } = useMemo(() => pushMuteInputsOf(cameras), [cameras])
  // undefined (loading, or failed with nothing loaded before) -> null: "not loaded"
  const myCameraFilters = useMemo(() => myCameraFiltersOf(rows), [rows])
  return { prefs, cameraFilters, mutedCameraIds, myCameraFilters }
}
